import { useEffect, useState } from 'react';
import axios from 'axios';
import { Plus, Loader2, Download, Settings, Users, Wallet } from 'lucide-react';
import { Input } from '../../components/ui/input';
import { API, isoDate, fmtDay } from './_helpers';

function TimesheetEditor({ t, onClose, onSaved }) {
  const [entries, setEntries] = useState(t.entries || []);
  const [busy, setBusy] = useState(false);

  const upd = (i, k, v) => setEntries(prev => prev.map((e, idx) => idx === i ? { ...e, [k]: v } : e));

  const save = async (alsoSubmit) => {
    setBusy(true);
    try {
      const patch = entries.map(e => ({
        date: e.date, actual_start: e.actual_start || null, actual_end: e.actual_end || null,
        breaks_minutes: parseInt(e.breaks_minutes || 0, 10), notes: e.notes || null,
      }));
      await axios.patch(`${API}/roster/timesheets/${t.timesheet_id}`, { entries: patch }, { withCredentials: true });
      if (alsoSubmit) await axios.post(`${API}/roster/timesheets/${t.timesheet_id}/submit`, null, { withCredentials: true });
      onSaved();
    } catch (e) { alert(e?.response?.data?.detail || 'Save failed'); setBusy(false); }
  };
  const readOnly = t.status === 'submitted' || t.status === 'approved';

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-xl max-w-3xl w-full max-h-[90vh] overflow-auto p-5 shadow-xl" onClick={e => e.stopPropagation()}>
        <h3 className="text-lg font-bold mb-1">Timesheet — {fmtDay(t.period_start)} → {fmtDay(t.period_end)}</h3>
        <p className="text-xs text-muted-foreground mb-3">{t.user_name} · <strong>{t.total_hours}h</strong> · status: {t.status}</p>
        <div className="border rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-muted/40 text-xs uppercase tracking-wider">
              <tr>
                <th className="text-left px-2 py-1.5">Date</th>
                <th className="text-left px-2 py-1.5">Rostered</th>
                <th className="text-left px-2 py-1.5">Actual start</th>
                <th className="text-left px-2 py-1.5">Actual end</th>
                <th className="text-left px-2 py-1.5">Break (min)</th>
                <th className="text-left px-2 py-1.5">Notes</th>
              </tr>
            </thead>
            <tbody>
              {entries.length === 0 && (
                <tr><td colSpan="6" className="text-center text-muted-foreground py-4">No shifts in this period.</td></tr>
              )}
              {entries.map((e, i) => (
                <tr key={i} className="border-t">
                  <td className="px-2 py-1.5 whitespace-nowrap">{fmtDay(e.date)}</td>
                  <td className="px-2 py-1.5 text-xs text-muted-foreground">{e.rostered_start && e.rostered_end ? `${e.rostered_start}–${e.rostered_end}` : '—'}</td>
                  <td className="px-2 py-1.5"><Input type="time" value={e.actual_start||''} onChange={ev => upd(i, 'actual_start', ev.target.value)} className="h-8 w-24" disabled={readOnly} /></td>
                  <td className="px-2 py-1.5"><Input type="time" value={e.actual_end||''} onChange={ev => upd(i, 'actual_end', ev.target.value)} className="h-8 w-24" disabled={readOnly} /></td>
                  <td className="px-2 py-1.5"><Input type="number" min="0" max="600" value={e.breaks_minutes||0} onChange={ev => upd(i, 'breaks_minutes', ev.target.value)} className="h-8 w-20" disabled={readOnly} /></td>
                  <td className="px-2 py-1.5"><Input value={e.notes||''} onChange={ev => upd(i, 'notes', ev.target.value)} placeholder="—" className="h-8" disabled={readOnly} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="flex justify-end gap-2 mt-4">
          <button onClick={onClose} className="text-sm font-semibold px-4 py-2 rounded-lg border">Close</button>
          {!readOnly && (
            <>
              <button onClick={() => save(false)} disabled={busy} className="text-sm font-semibold px-4 py-2 rounded-lg border" data-testid="timesheet-save">
                {busy ? 'Saving…' : 'Save draft'}
              </button>
              <button onClick={() => save(true)} disabled={busy} className="text-sm font-semibold px-4 py-2 rounded-lg bg-[#0d2644] text-white" data-testid="timesheet-submit">
                {busy ? 'Saving…' : 'Submit for approval'}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default function TimesheetsPanel({ isManager }) {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/roster/timesheets`, { withCredentials: true });
      setList(r.data.timesheets);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const generateMine = async () => {
    try {
      await axios.post(`${API}/roster/timesheets/generate`, { for_date: isoDate(new Date()) }, { withCredentials: true });
      load();
    } catch (e) { alert(e?.response?.data?.detail || 'Generate failed'); }
  };

  const decide = async (tid, decision) => {
    const notes = decision === 'rejected' ? window.prompt('Reason for rejection? (optional)') : '';
    try {
      await axios.post(`${API}/roster/timesheets/${tid}/decide`, { decision, notes }, { withCredentials: true });
      load();
    } catch (e) { alert(e?.response?.data?.detail || 'Decide failed'); }
  };

  const downloadCsv = (t) => {
    window.open(`${API}/roster/timesheets/${t.timesheet_id}/csv`, '_blank');
  };

  // Iter 144 — balances & pay-cycle
  const [balances, setBalances] = useState(null);
  const [cycle, setCycle]       = useState(null);
  const [showPayroll, setShowPayroll] = useState(false);
  useEffect(() => {
    (async () => {
      try {
        const c = await axios.get(`${API}/roster/settings/pay-cycle`, { withCredentials: true });
        setCycle(c.data);
      } catch { /* ignore */ }
      if (!isManager) {
        try {
          const me = await axios.get(`${API}/auth/me`, { withCredentials: true });
          const uid = me?.data?.user?.id || me?.data?.id;
          if (uid) {
            const b = await axios.get(`${API}/roster/users/${uid}/leave-balances`, { withCredentials: true });
            setBalances(b.data);
          }
        } catch { /* ignore */ }
      }
    })();
  }, [isManager]);

  return (
    <div>
      {cycle && (
        <div className="mb-3 rounded-lg border bg-card p-2.5 flex items-center justify-between flex-wrap gap-2" data-testid="paycycle-banner">
          <p className="text-xs text-muted-foreground">
            Pay cycle: <strong className="capitalize" style={{ color: '#0d2644' }}>{cycle.cycle_length}</strong>
            {' · '}Anchored to <strong>{cycle.anchor_date}</strong>
            {' · '}Reminder <strong>{cycle.reminder_hours_before}h</strong> before close
          </p>
          {isManager && (
            <button onClick={() => setShowPayroll(true)} data-testid="open-payroll-admin"
              className="text-xs font-semibold inline-flex items-center gap-1.5 px-2.5 py-1 rounded border">
              <Settings className="w-3.5 h-3.5" /> Payroll settings
            </button>
          )}
        </div>
      )}

      {!isManager && balances && (
        <div className="mb-3 rounded-lg border bg-card p-3 grid grid-cols-2 gap-3" data-testid="my-leave-balances">
          <div>
            <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Annual leave balance</p>
            <p className="text-xl font-bold" style={{ color: '#0d2644' }}>{Number(balances.annual_h || 0).toFixed(1)}h</p>
            <p className="text-[10px] text-muted-foreground">≈ {(Number(balances.annual_h || 0) / 7.6).toFixed(1)} days</p>
          </div>
          <div>
            <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Personal / sick leave</p>
            <p className="text-xl font-bold" style={{ color: '#0d2644' }}>{Number(balances.personal_h || 0).toFixed(1)}h</p>
            <p className="text-[10px] text-muted-foreground">≈ {(Number(balances.personal_h || 0) / 7.6).toFixed(1)} days</p>
          </div>
        </div>
      )}

      <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
        <h2 className="text-lg font-semibold" style={{ color: '#0d2644' }}>
          {isManager ? 'All timesheets' : 'My timesheets'}
        </h2>
        <div className="flex items-center gap-1.5 flex-wrap">
          <button onClick={generateMine} data-testid="timesheet-generate"
            className="text-sm font-semibold inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-lg border">
            <Plus className="w-4 h-4" /> Generate for current period
          </button>
          {isManager && (
            <XeroExportButton />
          )}
        </div>
      </div>
      {loading && <div className="text-center text-muted-foreground py-8"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div>}
      {!loading && !list.length && (
        <div className="text-center text-muted-foreground py-8 border rounded-xl bg-card">
          No timesheets yet. Click &ldquo;Generate&rdquo; once you&apos;ve got published shifts in a fortnight.
        </div>
      )}
      <div className="space-y-2">
        {list.map(t => (
          <div key={t.timesheet_id} className="border rounded-lg bg-card p-3" data-testid={`timesheet-${t.timesheet_id}`}>
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-xs font-bold uppercase tracking-wider px-2 py-0.5 rounded" style={{
                    background: t.status==='approved' ? '#dcfce7' : t.status==='rejected' ? '#fee2e2' :
                                t.status==='submitted' ? '#dbeafe' : '#f1f5f9',
                    color: t.status==='approved' ? '#15803d' : t.status==='rejected' ? '#b91c1c' :
                           t.status==='submitted' ? '#1d4ed8' : '#475569',
                  }}>{t.status}</span>
                  <span className="text-sm font-semibold" style={{ color: '#0d2644' }}>{t.user_name || t.user_email}</span>
                </div>
                <div className="text-xs text-muted-foreground mt-0.5">
                  {fmtDay(t.period_start)} → {fmtDay(t.period_end)} · <strong>{t.total_hours}h</strong>
                </div>
              </div>
              <div className="flex items-center gap-1.5">
                <button onClick={() => setEditing(t)} className="text-xs font-semibold px-2.5 py-1 rounded border" data-testid={`timesheet-open-${t.timesheet_id}`}>
                  {t.status === 'draft' ? 'Edit' : 'View'}
                </button>
                {isManager && t.status === 'submitted' && (
                  <>
                    <button onClick={() => decide(t.timesheet_id, 'approved')} className="text-xs font-semibold px-3 py-1.5 rounded bg-green-600 text-white"
                            data-testid={`timesheet-approve-${t.timesheet_id}`}>Approve</button>
                    <button onClick={() => decide(t.timesheet_id, 'rejected')} className="text-xs font-semibold px-3 py-1.5 rounded border border-red-600 text-red-600"
                            data-testid={`timesheet-reject-${t.timesheet_id}`}>Reject</button>
                  </>
                )}
                {isManager && (
                  <button onClick={() => downloadCsv(t)} className="text-xs font-semibold px-2.5 py-1 rounded border inline-flex items-center gap-1"
                          title="Download CSV for payroll" data-testid={`timesheet-csv-${t.timesheet_id}`}>
                    <Download className="w-3 h-3" /> CSV
                  </button>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
      {editing && <TimesheetEditor t={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
      {showPayroll && <PayrollAdminModal onClose={() => setShowPayroll(false)} onSaved={() => { setShowPayroll(false); load(); }} />}
    </div>
  );
}

// ─── Iter 144 — Xero payrun export ────────────────────────────────
function XeroExportButton() {
  const [start, setStart] = useState(() => {
    const d = new Date(); d.setDate(d.getDate() - 14);
    return isoDate(d);
  });
  const [end, setEnd] = useState(() => isoDate(new Date()));
  const [busy, setBusy] = useState(false);

  const download = async () => {
    setBusy(true);
    try {
      const res = await axios.get(`${API}/roster/timesheets/export/xero.csv`, {
        params: { start, end, only_approved: true },
        withCredentials: true, responseType: 'blob',
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url; a.download = `xero-payrun-${start}-to-${end}.csv`;
      a.click(); URL.revokeObjectURL(url);
    } catch (e) { alert(e?.response?.data?.detail || 'Xero export failed'); }
    finally { setBusy(false); }
  };

  return (
    <div className="flex items-center gap-1.5" data-testid="xero-export">
      <Input type="date" value={start} onChange={e => setStart(e.target.value)} className="h-8 text-xs w-36" data-testid="xero-start" />
      <span className="text-xs text-muted-foreground">→</span>
      <Input type="date" value={end} onChange={e => setEnd(e.target.value)} className="h-8 text-xs w-36" data-testid="xero-end" />
      <button onClick={download} disabled={busy} data-testid="xero-download"
        className="text-xs font-semibold inline-flex items-center gap-1.5 px-3 py-1.5 rounded border bg-[#13B5EA]/10 border-[#13B5EA] text-[#13B5EA]">
        {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
        Xero CSV
      </button>
    </div>
  );
}

// ─── Iter 144 — Payroll settings + contract editor + balances ────────
function PayrollAdminModal({ onClose, onSaved }) {
  const [tab, setTab] = useState('cycle');
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="payroll-admin-modal">
      <div className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-auto p-5 shadow-xl" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-xl font-bold" style={{ color: '#0d2644' }}>Payroll settings</h3>
          <button onClick={onClose} className="text-sm text-muted-foreground">✕</button>
        </div>
        <div className="flex items-center gap-1 border-b mb-4">
          {[['cycle', 'Pay cycle', Settings], ['contracts', 'Contracts', Users], ['balances', 'Leave balances', Wallet]].map(([k, l, Icon]) => (
            <button key={k} onClick={() => setTab(k)} data-testid={`payroll-tab-${k}`}
              className={`text-xs font-semibold inline-flex items-center gap-1.5 px-3 py-2 border-b-2 transition-colors ${
                tab === k ? 'border-teal-600 text-teal-700' : 'border-transparent text-muted-foreground'}`}>
              <Icon className="w-3.5 h-3.5" /> {l}
            </button>
          ))}
        </div>
        {tab === 'cycle'     && <PayCycleForm onSaved={onSaved} />}
        {tab === 'contracts' && <ContractsList />}
        {tab === 'balances'  && <BalancesList />}
      </div>
    </div>
  );
}

function PayCycleForm({ onSaved }) {
  const [cycle, setCycle] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    axios.get(`${API}/roster/settings/pay-cycle`, { withCredentials: true }).then(r => setCycle(r.data));
  }, []);
  if (!cycle) return <Loader2 className="w-5 h-5 animate-spin mx-auto my-8" />;
  const save = async () => {
    setBusy(true);
    try {
      await axios.patch(`${API}/roster/settings/pay-cycle`, {
        cycle_length: cycle.cycle_length,
        anchor_date: cycle.anchor_date,
        reminder_hours_before: parseInt(cycle.reminder_hours_before, 10),
      }, { withCredentials: true });
      onSaved();
    } catch (e) { alert(e?.response?.data?.detail || 'Save failed'); }
    finally { setBusy(false); }
  };
  return (
    <div className="space-y-3">
      <label className="block">
        <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Cycle length</span>
        <select value={cycle.cycle_length} onChange={e => setCycle({ ...cycle, cycle_length: e.target.value })}
          data-testid="paycycle-length"
          className="w-full mt-1 text-sm border rounded-lg px-3 py-2">
          <option value="weekly">Weekly (7 days)</option>
          <option value="fortnightly">Fortnightly (14 days) — most common</option>
          <option value="monthly">Monthly (calendar month)</option>
        </select>
      </label>
      <label className="block">
        <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Anchor date (Monday recommended for weekly/fortnightly)</span>
        <Input type="date" value={cycle.anchor_date} onChange={e => setCycle({ ...cycle, anchor_date: e.target.value })}
          data-testid="paycycle-anchor" />
      </label>
      <label className="block">
        <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Reminder hours before cycle close</span>
        <Input type="number" min="1" max="168" value={cycle.reminder_hours_before}
          onChange={e => setCycle({ ...cycle, reminder_hours_before: e.target.value })}
          data-testid="paycycle-reminder-hours" />
      </label>
      <button onClick={save} disabled={busy} data-testid="paycycle-save"
        className="text-sm font-semibold px-4 py-2 rounded-lg bg-teal-600 text-white inline-flex items-center gap-2">
        {busy && <Loader2 className="w-4 h-4 animate-spin" />} Save pay cycle
      </button>
    </div>
  );
}

function ContractsList() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/users`, { withCredentials: true });
        setUsers(r.data.users || r.data || []);
      } finally { setLoading(false); }
    })();
  }, []);
  if (loading) return <Loader2 className="w-5 h-5 animate-spin mx-auto my-8" />;
  return (
    <>
      <div className="border rounded-lg overflow-hidden" data-testid="contracts-list">
        <table className="w-full text-sm">
          <thead className="bg-muted/40 text-xs uppercase tracking-wider">
            <tr>
              <th className="text-left px-2 py-1.5">Name</th>
              <th className="text-left px-2 py-1.5">Type</th>
              <th className="text-left px-2 py-1.5">Weekly h</th>
              <th className="text-left px-2 py-1.5">Rate</th>
              <th className="text-left px-2 py-1.5">Xero code</th>
              <th className="text-left px-2 py-1.5"></th>
            </tr>
          </thead>
          <tbody>
            {users.map(usr => {
              const c = usr.contract || {};
              return (
                <tr key={usr.id} className="border-t" data-testid={`contract-row-${usr.id}`}>
                  <td className="px-2 py-1.5"><strong>{usr.name}</strong><div className="text-[10px] text-muted-foreground">{usr.email}</div></td>
                  <td className="px-2 py-1.5">{c.employment_type || <span className="text-muted-foreground">—</span>}</td>
                  <td className="px-2 py-1.5">{c.ordinary_hours_per_week ?? <span className="text-muted-foreground">—</span>}</td>
                  <td className="px-2 py-1.5">{c.base_hourly_rate ? `$${c.base_hourly_rate}` : <span className="text-muted-foreground">—</span>}</td>
                  <td className="px-2 py-1.5 font-mono text-xs">{c.xero_employee_code || <span className="text-muted-foreground">—</span>}</td>
                  <td className="px-2 py-1.5 text-right">
                    <button onClick={() => setEditing(usr)} data-testid={`edit-contract-${usr.id}`}
                      className="text-xs font-semibold px-2 py-1 rounded border">Edit</button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {editing && <ContractEditor user={editing} onClose={() => setEditing(null)} onSaved={async () => {
        const r = await axios.get(`${API}/users`, { withCredentials: true });
        setUsers(r.data.users || r.data || []); setEditing(null);
      }} />}
    </>
  );
}

function ContractEditor({ user, onClose, onSaved }) {
  const [c, setC] = useState({
    employment_type:         user.contract?.employment_type         || 'full_time',
    ordinary_hours_per_week: user.contract?.ordinary_hours_per_week ?? 38,
    base_hourly_rate:        user.contract?.base_hourly_rate        ?? 0,
    employment_start_date:   user.contract?.employment_start_date   || '',
    xero_employee_code:      user.contract?.xero_employee_code      || '',
  });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      await axios.patch(`${API}/roster/users/${user.id}/contract`, {
        employment_type: c.employment_type,
        ordinary_hours_per_week: parseFloat(c.ordinary_hours_per_week),
        base_hourly_rate: parseFloat(c.base_hourly_rate),
        employment_start_date: c.employment_start_date || null,
        xero_employee_code: c.xero_employee_code || null,
      }, { withCredentials: true });
      onSaved();
    } catch (e) { alert(e?.response?.data?.detail || 'Save failed'); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/60 z-[60] flex items-center justify-center p-4" onClick={onClose} data-testid="contract-editor">
      <div className="bg-white rounded-xl max-w-md w-full p-5 shadow-xl" onClick={e => e.stopPropagation()}>
        <h4 className="text-lg font-bold mb-3" style={{ color: '#0d2644' }}>{user.name} — Contract</h4>
        <div className="space-y-3">
          <label className="block">
            <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Employment type</span>
            <select value={c.employment_type} onChange={e => setC({ ...c, employment_type: e.target.value })}
              data-testid="contract-type" className="w-full mt-1 text-sm border rounded-lg px-3 py-2">
              <option value="full_time">Full-time</option>
              <option value="part_time">Part-time</option>
              <option value="casual">Casual (no accruals · 25% loading)</option>
            </select>
          </label>
          <label className="block">
            <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Ordinary hours per week</span>
            <Input type="number" step="0.5" min="0" max="80" value={c.ordinary_hours_per_week}
              onChange={e => setC({ ...c, ordinary_hours_per_week: e.target.value })}
              data-testid="contract-hours" />
          </label>
          <label className="block">
            <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Base hourly rate ($ AUD)</span>
            <Input type="number" step="0.01" min="0" value={c.base_hourly_rate}
              onChange={e => setC({ ...c, base_hourly_rate: e.target.value })}
              data-testid="contract-rate" />
          </label>
          <label className="block">
            <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Start date</span>
            <Input type="date" value={c.employment_start_date}
              onChange={e => setC({ ...c, employment_start_date: e.target.value })}
              data-testid="contract-start" />
          </label>
          <label className="block">
            <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Xero employee code (optional)</span>
            <Input value={c.xero_employee_code}
              onChange={e => setC({ ...c, xero_employee_code: e.target.value })}
              data-testid="contract-xero-code"
              placeholder="e.g. EMP001 — leave blank to use email" />
          </label>
        </div>
        <div className="flex items-center justify-end gap-2 mt-4">
          <button onClick={onClose} className="text-xs font-semibold px-3 py-1.5 rounded border">Cancel</button>
          <button onClick={save} disabled={busy} data-testid="contract-save"
            className="text-xs font-semibold px-3 py-1.5 rounded bg-teal-600 text-white inline-flex items-center gap-2">
            {busy && <Loader2 className="w-3.5 h-3.5 animate-spin" />} Save
          </button>
        </div>
      </div>
    </div>
  );
}

function BalancesList() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/users`, { withCredentials: true });
        const list = r.data.users || r.data || [];
        const withBal = await Promise.all(list.map(async u => {
          try {
            const b = await axios.get(`${API}/roster/users/${u.id}/leave-balances`, { withCredentials: true });
            return { ...u, balances: b.data };
          } catch { return { ...u, balances: null }; }
        }));
        setUsers(withBal);
      } finally { setLoading(false); }
    })();
  }, []);
  if (loading) return <Loader2 className="w-5 h-5 animate-spin mx-auto my-8" />;
  return (
    <div className="border rounded-lg overflow-hidden" data-testid="balances-list">
      <table className="w-full text-sm">
        <thead className="bg-muted/40 text-xs uppercase tracking-wider">
          <tr>
            <th className="text-left px-2 py-1.5">Name</th>
            <th className="text-left px-2 py-1.5">Annual (h)</th>
            <th className="text-left px-2 py-1.5">Personal (h)</th>
            <th className="text-left px-2 py-1.5">Last accrual</th>
          </tr>
        </thead>
        <tbody>
          {users.map(u => (
            <tr key={u.id} className="border-t" data-testid={`balance-row-${u.id}`}>
              <td className="px-2 py-1.5"><strong>{u.name}</strong><div className="text-[10px] text-muted-foreground">{u.email}</div></td>
              <td className="px-2 py-1.5">{Number(u.balances?.annual_h || 0).toFixed(2)}</td>
              <td className="px-2 py-1.5">{Number(u.balances?.personal_h || 0).toFixed(2)}</td>
              <td className="px-2 py-1.5 text-[10px] text-muted-foreground">{u.balances?.last_accrued_at?.slice(0, 10) || '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
